import { useMemo } from 'react';
import { DivergingMeter } from './DivergingMeter';
import { HolderColumn } from './HolderColumn';

const parseStake = (stake) => {
  if (!stake) return 0;
  if (typeof stake === 'number') return stake;
  if (typeof stake !== 'string') return 0;

  const lowerStake = stake.toLowerCase().replace(/[$,]/g, '').trim();
  
  let multiplier = 1;
  if (lowerStake.endsWith('k')) multiplier = 1000;
  else if (lowerStake.endsWith('m')) multiplier = 1000000;
  else if (lowerStake.endsWith('b')) multiplier = 1000000000;

  const numericPart = parseFloat(lowerStake);
  
  if (isNaN(numericPart)) return 0;

  return numericPart * multiplier;
};

export function MarketCard({ market, primaryCategory }) {
  const smartStats = useMemo(() => {
    if (!market?.holders) {
      return {
        yesCountPercent: 0,
        noCountPercent: 0,
        yesValuePercent: 0,
        noValuePercent: 0
      };
    }

    const yesCount = market.holders.yes?.length || 0;
    const noCount = market.holders.no?.length || 0;
    const totalCount = yesCount + noCount || 1;

    const yesValue = (market.holders.yes || []).reduce((sum, h) => sum + parseStake(h.stake), 0);
    const noValue = (market.holders.no || []).reduce((sum, h) => sum + parseStake(h.stake), 0);
    const totalValue = yesValue + noValue || 1;

    return {
      yesCountPercent: Math.round((yesCount / totalCount) * 100),
      noCountPercent: Math.round((noCount / totalCount) * 100),
      yesValuePercent: Math.round((yesValue / totalValue) * 100),
      noValuePercent: Math.round((noValue / totalValue) * 100)
    };
  }, [market]);

  const maxHolderStake = useMemo(() => {
    if (!market?.holders) return 0;
    const allHolders = [...(market.holders.yes || []), ...(market.holders.no || [])];
    return Math.max(...allHolders.map((h) => parseStake(h.stake)), 0);
  }, [market]);

  if (!market) {
    return (
      <section className="glass-lg w-full overflow-hidden">
        <div className="p-6 md:p-10 lg:p-10">
          <div className="p-6 rounded-2xl bg-slate-900/70 border border-slate-600/55 text-slate-300">
            暂无推荐市场数据
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="glass-lg market-card-bg w-full overflow-hidden"
      style={market.image ? { '--bg-image': `url(${market.image})` } : {}}
    >
      <div className="p-6 md:p-10 lg:p-10 flex flex-col gap-8">
        {/* Market Head */}
        <div>
          <div>
            <p className="uppercase tracking-[0.3em] text-[0.6rem] md:text-xs text-text-muted font-medium">{primaryCategory}</p>
            <h1 className="text-2xl md:text-3xl lg:text-4xl my-2 font-semibold text-white leading-tight">{market.title}</h1>
            {market.subtitle && (
              <p className="text-slate-300/80 text-sm md:text-base leading-relaxed mt-2">{market.subtitle}</p>
            )}
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="stat-card-volume p-4 rounded-2xl flex flex-col gap-1.5">
            <p className="text-text-muted text-[0.65rem] md:text-xs font-medium">24小时交易额</p>
            <strong className="text-sm md:text-base text-text">{market.metrics.volume}</strong>
          </div>
          <div className="stat-card-deadline p-4 rounded-2xl flex flex-col gap-1.5">
            <p className="text-text-muted text-[0.65rem] md:text-xs font-medium">截止时间</p>
            <strong className="text-sm md:text-base text-text">{market.deadline}</strong>
          </div>
        </div>

        {/* Meters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <div className="flex items-center mb-3">
              <p className="text-white font-semibold text-base md:text-lg">市场概率</p>
            </div>
            <DivergingMeter
              yesValue={market.probYes}
              noValue={market.probNo}
              yesLabel={`Yes ${market.probYes}%`}
              noLabel={`${market.probNo}% No`}
            />
          </div>
          <div>
            <div className="flex items-center mb-3">
              <p className="text-white font-semibold text-base md:text-lg">聪明钱（地址数量）</p>
            </div>
            <DivergingMeter
              yesValue={smartStats.yesCountPercent}
              noValue={smartStats.noCountPercent}
              yesLabel={`Yes ${smartStats.yesCountPercent}%`}
              noLabel={`${smartStats.noCountPercent}% No`}
            />
          </div>
          <div>
            <div className="flex items-center mb-3">
              <p className="text-white font-semibold text-base md:text-lg">聪明钱（持仓金额）</p>
            </div>
            <DivergingMeter
              yesValue={smartStats.yesValuePercent}
              noValue={smartStats.noValuePercent}
              yesLabel={`Yes ${smartStats.yesValuePercent}%`}
              noLabel={`${smartStats.noValuePercent}% No`}
            />
          </div>
        </div>

        {/* Holders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <HolderColumn
            title="Yes 方向 · 聪明钱持有者"
            titleClassName="text-white font-semibold text-base md:text-lg mb-3"
            color="emerald"
            items={market.holders.yes}
            maxStake={maxHolderStake}
          />
          <HolderColumn
            title="No 方向 · 聪明钱持有者"
            titleClassName="text-white font-semibold text-base md:text-lg mb-3"
            color="rose"
            items={market.holders.no}
            maxStake={maxHolderStake}
          />
        </div>
      </div>
    </section>
  );
}
